import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import { withRouter, Link } from 'react-router';
import cn from 'classnames';
import { get, isEmpty } from 'lodash';

import Icon from 'chameleon/ui-stack/ui/base/icon';
import AssignmentSection from 'legacy-src/components/assessment/Assessments/AssignmentSection';
import VideoProgressBarPopover from 'chameleon/ui-stack/ui/assessments/video-progress-bar/VideoProgressBarPopover';
import SPProgressBarPopover from 'chameleon/ui-stack/ui/assessments/spp-progress-bar/SPPProgressBarPopover';
import buildSubgroupStudents from 'chameleon/ui-stack/utilities/utils/buildSubgroupStudents';
import PopoverActionButton from 'chameleon/legacy/packages/unit-outline/src/components/AssessmentProgress/PopoverActionButton';
import Button from 'chameleon/ui-stack/ui/base/button';
import AssignItem from 'chameleon/legacy/src/components/assessment/AssignItem';
import { getPriorStudentsSubdir } from 'chameleon/legacy/src/utility/utils';
import { computeAssignmentsReadyToScore } from 'chameleon/src/components/learnosity/utils/helpers';
import connectLeanUpdateAssignments from 'chameleon/src/utility/connectLeanUpdateAssignments';
import { connectLean } from 'lean-redux';
import LockedProgressBar from 'chameleon/legacy/packages/unit-outline/src/components/AssessmentProgress/LockedProgressBar';
import Results from 'chameleon/ui-stack/ui/assessments/assignment-results';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

export const SUBMISSIONS_TO_SCORE_TEXT = 'to Score';
const VIEW_RESULTS_TEXT = 'View Results';
const MANAGE_ASSIGNMENT_TEXT = 'Manage Assignment';

const computeIsComplete = (progress, assignment, status, totalSubmissions) => {
  const scoredSubmissions =
    progress.complete +
    progress.scored +
    progress.teacher_and_student_scored +
    progress.student_scored;
  return status === 'closed' || scoredSubmissions === totalSubmissions;
};

const noDataPlaceholder = <span className="mb-0">—</span>;
/**
 * Draws progress popover content
 * */
function ProgressPopoverContent(props) {
  const {
    assessment = {},
    learnosityReportByUsers,
    studentsBySection,
    mappedGroups,
    basePathname,
    router,
    params: { subject: subjectId },
    routes,
    saveAssignedState,
    useNewAssignModalFlag,
  } = props;

  const { performanceTiers = {} } = usePerformanceTiers(subjectId);

  const assessmentPathname = basePathname.replace('assignments', 'assessments');
  const { type = '', is_assigned: isAssigned, assignments } = assessment || {};
  const isVideo = type === 'video';
  const isSPP = type === 'spp';
  const isPPC = type === 'ppc';
  const sortedByClass = assignments.sort((a, b) => {
    if (a.section_name.toUpperCase() > b.section_name.toUpperCase()) {
      return 1;
    }
    if (a.section_name.toUpperCase() < b.section_name.toUpperCase()) {
      return -1;
    }
    return 0;
  });

  const getResultBar = (assignment, isComplete, onProgressBarClick) => {
    if (isVideo) {
      return (
        <VideoProgressBarPopover
          progress={assignment.progress}
          isUnassigned={!isAssigned}
          withoutPopover
          disabled
        />
      );
    }

    if (isSPP) {
      return (
        <SPProgressBarPopover
          students={get(studentsBySection, assignment.section_id, [])}
          isUnassigned={!isAssigned}
          assignment={assignment || {}}
          fromTeacherAssignmentsTable
          isActivePopover={false}
        />
      );
    }
    const {
      progress,
      students = [],
      teacher_assigned: teacherAssigned,
      starts_at: startsAt,
      due_at: dueAt,
    } = assignment;

    const isScored =
      progress.student_scored +
        progress.teacher_and_student_scored +
        progress.scored +
        progress.complete >
      0;
    const assignmentProgressLink = `/${subjectId}/assignments/progress/${
      assignment.id
    }${getPriorStudentsSubdir(routes)}`;
    const notScoredContainerRow = (
      <Link to={assignmentProgressLink}>
        <LockedProgressBar
          data-test-progress-bar
          isSmall
          assignmentProgress={progress}
          isScheduled={!!teacherAssigned}
          startsAt={startsAt}
          dueAt={dueAt}
          hasStudents={students.length > 0}
        />
      </Link>
    );
    if (!isScored) {
      return <div className="scored-container">{notScoredContainerRow}</div>;
    }
    return (
      <Results
        id={assignment.id}
        result={assignment || {}}
        groups={mappedGroups}
        isComplete={isComplete}
        isVideo={isVideo}
        performanceTiers={performanceTiers}
        onProgressBarClick={onProgressBarClick}
        studentsBySection={studentsBySection}
        popUpDisabled
      />
    );
  };

  return (
    <div className="rounded-2xl">
      <div className="flex flex-row justify-between border-b border-gray-300">
        <div className="text-2xl font-bold text-black text-left p-4 flex flex-1">
          <span className={cn('w-128')}>
            {get(HEADERS, type.toUpperCase(), false) || assessment.title}
          </span>
          <span aria-hidden className="w-48 ml-2">
            Assigned on
          </span>
        </div>
      </div>
      <div className="divide-y divide-gray-300 flex flex-col">
        {sortedByClass.map((assignment) => {
          const {
            report,
            progress,
            students_who_submitted_spp: submittedStudents,
            status,
            teacher_assigned: teacherAssigned,
            scramble_questions: scrambleQuestions,
            is_secure: isSecure,
            section_id: sectionId,
          } = assignment || {};
          const {
            submitted,
            complete,
            scored,
            student_scored,
            teacher_and_student_scored,
            mcq_scoring,
            watched,
            total_students,
          } = progress || {};
          let totalSubmissions =
            assignment &&
            Object.values(progress).reduce((curr, next) => curr + next, 0);
          let submittedSum =
            mcq_scoring +
            submitted +
            complete +
            scored +
            student_scored +
            teacher_and_student_scored;
          let isComplete = computeIsComplete(
            assignment.progress,
            assignment,
            status,
            totalSubmissions,
          );
          const assignmentProgressLink = `/${subjectId}/assignments/progress/${
            assignment.id
          }${getPriorStudentsSubdir(routes)}`;

          const assignmentsReadyToScore = computeAssignmentsReadyToScore(
            assignment.progress,
          );

          const onAssignItem = (assignmentsResponse, totalAssignees) => {
            connectLeanUpdateAssignments({
              assignmentsResponse,
              totalAssignees,
              saveAssignedState,
              useNewAssignModalFlag,
              studentsBySection,
            });
          };

          const assignmentResultsLink = `/${subjectId}/assessments/results/${
            assignment.id
          }${getPriorStudentsSubdir(routes)}`;

          const selectedGroups = {
            assign: [Number.parseInt(sectionId, 10)],
          };

          const assignBtn = (
            <Button
              isPrimary
              tabIndex={0}
              accessibility={{ 'aria-label': 'Unlock Schedule' }}
            >
              Unlock / Schedule
            </Button>
          );

          const viewResults = (
            <PopoverActionButton
              data-test-view-results-button
              actionLink={assignmentResultsLink}
            >
              {VIEW_RESULTS_TEXT}
            </PopoverActionButton>
          );

          const isScored =
            progress.student_scored +
              progress.teacher_and_student_scored +
              progress.scored +
              progress.complete >
            0;
          if (isVideo) {
            submittedSum = watched;
            isComplete = total_students > 0 && total_students === watched;
          } else if (isSPP) {
            const hasScoredResult = report && !isEmpty(report);
            const reportByUsers =
              hasScoredResult && learnosityReportByUsers([report]);
            const reportUsers = !isEmpty(reportByUsers)
              ? Object.keys(reportByUsers)
              : [];
            const apcLRNSubmittedIntersection = submittedStudents.filter(
              (value) => reportUsers.includes(String(value)),
            );
            submittedSum = apcLRNSubmittedIntersection.length;
            isComplete =
              status === 'closed' || submittedSum === totalSubmissions;
          }
          const assignmentProgressURL = `${basePathname}/progress/${assignment.id}`;
          const isNotAssigned = !teacherAssigned;
          const isLocked = status === 'locked';
          const assignedOn =
            teacherAssigned && new Date(teacherAssigned).toLocaleDateString();
          const progressBarUrl =
            submittedSum > 0
              ? `${assessmentPathname}/results/${assignment.id}/questions`
              : assignmentProgressURL;

          const onProgressBarClick = () => {
            router.push(progressBarUrl);
          };

          return (
            <div
              key={assignment.id}
              className="flex items-center justify-start w-full p-4"
            >
              {isPPC && (
                <div className="mx-2">
                  <Icon
                    id={isNotAssigned || isLocked ? 'lock' : 'unlock'}
                    className="fill-current text-black"
                  />
                </div>
              )}
              <div
                className={cn('mx-2', {
                  'w-128': !isPPC,
                  'w-110': isPPC,
                })}
              >
                <AssignmentSection
                  sectionName={assignment.section_name}
                  subGroupStudents={buildSubgroupStudents(
                    assignment.sub_group_students,
                    studentsBySection,
                  )}
                  noDataPlaceholder={noDataPlaceholder}
                  assignmentProgressURL={assignmentProgressURL}
                  removeMargin
                  disableStudentsPopover
                />
              </div>
              <div className="w-32 mr-8">
                {assignedOn ? (
                  <span>
                    <span className="sr-only">Assigned on</span> {assignedOn}
                  </span>
                ) : (
                  <span>&mdash;</span>
                )}
              </div>
              <div
                className={cn('progress-bar-container', {
                  'cursor-pointer': !isVideo,
                })}
                {...(!isVideo && {
                  tabIndex: 0,
                  'aria-label': 'Open progress bar',
                  role: 'link',
                })}
              >
                {getResultBar(assignment, isComplete, onProgressBarClick)}
              </div>
              {!isVideo && !isSPP && (
                <div className="actions-container ml-auto pl-20 mr-4 flex shrink-0 grow-0 items-center">
                  {(() => {
                    if (isNotAssigned) {
                      return (
                        <AssignItem
                          selectedGroups={selectedGroups}
                          groups={[]}
                          itemId={Number.parseInt(assessment.id, 10)}
                          itemTitle={assignment.title}
                          hideSecureToggle={!isSecure}
                          onAssign={onAssignItem}
                          onAssignRedirect={false}
                          scrambleable={scrambleQuestions}
                          button={assignBtn}
                        />
                      );
                    }
                    if (!isScored) {
                      return (
                        <PopoverActionButton
                          data-test-score-submissions-button
                          actionLink={assignmentProgressLink}
                        >
                          {assignmentsReadyToScore > 0 && !assessment.isSPP
                            ? `${assignmentsReadyToScore} ${SUBMISSIONS_TO_SCORE_TEXT}`
                            : MANAGE_ASSIGNMENT_TEXT}
                        </PopoverActionButton>
                      );
                    }
                    return (
                      <Fragment>
                        {(() => {
                          if (
                            assignmentsReadyToScore > 0 &&
                            !assessment.isSPP
                          ) {
                            return (
                              <Fragment>
                                <div className="mr-4">
                                  <Link to={assignmentResultsLink}>
                                    <Button
                                      tabIndex={0}
                                      accessibility={{
                                        'aria-label': VIEW_RESULTS_TEXT,
                                      }}
                                      isLink
                                      isNaked
                                    >
                                      {VIEW_RESULTS_TEXT}
                                    </Button>
                                  </Link>
                                </div>
                                <PopoverActionButton
                                  data-test-score-submissions-button
                                  actionLink={assignmentProgressLink}
                                >
                                  {`${assignmentsReadyToScore} ${SUBMISSIONS_TO_SCORE_TEXT}`}
                                </PopoverActionButton>
                              </Fragment>
                            );
                          }
                          return (
                            <Fragment>
                              <Fragment>
                                <div className="mr-2">
                                  <Link to={assignmentProgressLink}>
                                    <Button
                                      tabIndex={0}
                                      accessibility={{
                                        'aria-label': MANAGE_ASSIGNMENT_TEXT,
                                      }}
                                      isLink
                                      isNaked
                                    >
                                      {MANAGE_ASSIGNMENT_TEXT}
                                    </Button>
                                  </Link>
                                </div>

                                {viewResults}
                              </Fragment>
                            </Fragment>
                          );
                        })()}
                      </Fragment>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const HEADERS = {
  PE: 'Practice Exam (All Progress)',
};

export default compose(
  withRouter,
  connect(({ featureFlag, ...state }) => ({
    showAttestationConfirmationDialog:
      featureFlag.show_attestation_confirmation_dialog,
    disablePpcAutoAssignFlag: featureFlag.disable_ppc_auto_assign,
    subjectData: state.subject.data,
    useNewAssignModalFlag: featureFlag.new_assign_modal,
  })),
  connectLean({
    saveAssignedState(updatedAssignmentsInfo) {
      const { assignmentOverview = {} } = this.state;
      const { assignments = [] } = assignmentOverview;
      this.setState({
        assignmentOverview: {
          ...assignmentOverview,
          assignments: [...assignments, ...updatedAssignmentsInfo],
        },
      });
    },
  }),
)(ProgressPopoverContent);
